import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import './GlobalCustomTable.scss';
import BootstrapTable from 'react-bootstrap-table-next';
import { globalTableSearchProps } from '../components/Common/SearchBox/MySearch';
import _debounce from 'lodash/debounce';
import paginationFactory, {
    PaginationProvider,
    PaginationListStandalone,
    SizePerPageDropdownStandalone,
    PaginationTotalStandalone
} from 'react-bootstrap-table2-paginator';


const customTotal = (from, to, size) => (
    <span
        className="react-bootstrap-table-pagination-total"
        style={{ display: size > 0 ? 'block' : 'none' }}
    >
        Showing {from} to {to} of {size} Results
    </span>
);


const GlobalCustomTable = ({
    data,
    columns,
    keyField,
    defaultSearchText,
    stablePagination = false,
    controlledPagination = true,
    classes,
    updatedRowBlinkId,
    lazyLoading = false,
    onDataSizeChange,
    paginationEnabled = false,
    isPaginationTotalStandalone = true,
    hideEmptyGroupHeadersOnSearch = false, // This Prop is for Group SubGrop row hiding on UI like itemmasterbulkupdate page.
    ...rest
}) => {
    const updatedRowBlinkIds_string = updatedRowBlinkId?.toString() || '';

    const [searchText, setSearchText] = useState(defaultSearchText || '');
    const [currentPage, setCurrentPage] = useState(1);

    const isLazy = Boolean(lazyLoading && !paginationEnabled);
    const CHUNK_SIZE =
        typeof lazyLoading === 'number' ? lazyLoading : 50;

    const [visibleCount, setVisibleCount] = useState(
        isLazy ? CHUNK_SIZE : data.length
    );

    const sentinelRef = React.useRef(null);
    const [isLoading, setIsLoading] = useState(false);


    const debounceHandleSearch = _debounce((val) => {
        setCurrentPage(1);
        setSearchText(val);
    }, 300);


    useEffect(() => {
        globalTableSearchProps({
            onSearch: debounceHandleSearch
        });
    });


    const rowClasses = (row) => {
        let cs = '';

        if (row.IsRecordDeleted) {
            cs += '_deleted-Row ';
        }

        if (updatedRowBlinkIds_string.includes(row[keyField])) {
            cs += '_row-blink ';
        }

        return cs;
    };


    const filteredData = useMemo(() => {

        // Existing search logic
        const searchedData = data.filter((row) =>
            columns.some((column) => {
                const columnValue = row[column.dataField];
                const isHidden = column.hidden;

                if (
                    columnValue === null ||
                    isHidden ||
                    columnValue === undefined
                ) {
                    return false;
                }

                return columnValue
                    .toString()
                    .toLowerCase()
                    .includes(searchText.toLowerCase());
            })
        );

        // If this feature is NOT enabled,
        // return the original search result exactly as before.
        if (!hideEmptyGroupHeadersOnSearch) {
            return searchedData;
        }

        // No search = original behaviour
        if (!searchText.trim()) {
            return searchedData;
        }

        // ------------------------------------------------------------
        // Your Item Master specific Group/SubGroup logic
        // ------------------------------------------------------------

        const result = [];

        for (let i = 0; i < searchedData.length; i++) {

            const row = searchedData[i];

            // Real item row
            if (!row.GroupRow && !row.SubGroupRow) {
                result.push(row);
                continue;
            }

            // SubGroup header
            if (row.SubGroupRow) {

                // Don't show null/empty Group/SubGroup
                if (
                    row.SubGroupName == null ||
                    row.SubGroupName === "" ||
                    row.SubGroupName === "null" ||
                    row.Group_Subgroup === "null-null" ||
                    row.Group_Subgroup === "undefined-undefined"
                ) {
                    continue;
                }

                // Find the actual item immediately belonging
                // to this header
                let nextItem = null;

                for (let j = i + 1; j < searchedData.length; j++) {

                    const nextRow = searchedData[j];

                    if (
                        nextRow.GroupRow ||
                        nextRow.SubGroupRow
                    ) {
                        break;
                    }

                    nextItem = nextRow;
                    break;
                }

                if (!nextItem) {
                    continue;
                }

                const itemKey =
                    `${nextItem.GroupName ?? "null"}-${nextItem.SubGroupName ?? "null"}`;

                if (itemKey === row.Group_Subgroup) {

                    const previousRow = result[result.length - 1];

                    if (
                        previousRow?.SubGroupRow &&
                        previousRow.Group_Subgroup === row.Group_Subgroup
                    ) {
                        continue;
                    }

                    result.push(row);
                }

                continue;
            }

            // Any other row type
            result.push(row);
        }

        return result;

    }, [
        data,
        searchText,
        columns,
        hideEmptyGroupHeadersOnSearch
    ]);


    const handleDataChange = (sizePerPage, page) => {
        setCurrentPage(page);

        if (onDataSizeChange) {
            onDataSizeChange({
                dataCount: filteredData.length,
                filteredData: filteredData
            });
        }
    };


    const sizePerPage =
        parseInt(paginationEnabled) > 0
            ? paginationEnabled
            : 25;


    /*
     * Pagination options.
     *
     * controlledPagination = true
     * → Existing GlobalCustomTable behavior.
     *   currentPage controls the active page.
     *
     * controlledPagination = false
     * → SchemeItemGlobalTable behavior.
     *   PaginationProvider manages the active page internally.
     */
    const paginationOptions = {
        ...(controlledPagination ? { page: currentPage } : {}),
        paginationSize: 5,
        pageStartIndex: 1,
        sizePerPage: paginationEnabled
            ? sizePerPage
            : filteredData.length + 10,
        custom: true,
        totalSize: filteredData.length,
        hidePageListOnlyOnePage: true,
        paginationTotalRenderer: customTotal,
    };


    /*
     * Stable pagination.
     *
     * Used only when stablePagination={true}.
     * This prevents paginationFactory from creating a new pagination
     * instance on unrelated row-level updates.
     */
    const memoizedPagination = useMemo(
        () => paginationFactory(paginationOptions),
        [
            controlledPagination,
            currentPage,
            paginationEnabled,
            sizePerPage,
            filteredData.length
        ]
    );


    /*
     * stablePagination={false}
     * → Preserve old GlobalCustomTable behavior.
     *
     * stablePagination={true}
     * → Preserve SchemeItemGlobalTable stable behavior.
     */
    const pagination = stablePagination
        ? memoizedPagination
        : paginationFactory(paginationOptions);


    const sortCaretFunction = {
        sortCaret: (order, column) => {
            if (!order) {
                return (
                    <i
                        className="fas fa-sort-up pl-1 font-size-12"
                        style={{ color: "#e9e9ef" }}
                    ></i>
                );
            }

            if (order === 'asc') {
                return (
                    <i className="fas fa-sort-up pl-1 font-size-12"></i>
                );
            }

            if (order === 'desc') {
                return (
                    <i className="fas fa-sort-down pl-1 font-size-12"></i>
                );
            }

            return (
                <i
                    className="fas fa-sort-up pl-1 font-size-12"
                    style={{ color: "#e9e9ef" }}
                ></i>
            );
        }
    };


    useEffect(() => {
        if (!isLazy) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (
                    entries[0].isIntersecting &&
                    visibleCount < filteredData.length
                ) {
                    setIsLoading(true);

                    setTimeout(() => {
                        setVisibleCount((prev) =>
                            Math.min(
                                prev + CHUNK_SIZE,
                                filteredData.length
                            )
                        );

                        setIsLoading(false);
                    }, 50);
                }
            },
            {
                root: null,
                rootMargin: '0px',
                threshold: 1.0
            }
        );

        if (sentinelRef.current) {
            observer.observe(sentinelRef.current);
        }

        return () => {
            if (sentinelRef.current) {
                observer.unobserve(sentinelRef.current);
            }
        };
    }, [
        lazyLoading,
        paginationEnabled,
        visibleCount,
        filteredData,
        CHUNK_SIZE,
        isLazy
    ]);


    return (
        <PaginationProvider
            data={filteredData}
            columns={columns}
            keyField={keyField}
            pagination={pagination}
        >
            {({
                paginationProps,
                paginationTableProps
            }) => (
                <div>
                    <BootstrapTable
                        data={
                            paginationEnabled
                                ? filteredData
                                : isLazy
                                    ? filteredData.slice(
                                        0,
                                        visibleCount
                                    )
                                    : filteredData
                        }
                        columns={columns}
                        keyField={keyField}
                        classes="custom-table "
                        rowClasses={rowClasses}
                        sort={sortCaretFunction}
                        onDataSizeChange={handleDataChange}
                        {...rest}
                        {...paginationTableProps}
                        bootstrap4
                    />

                    <div ref={sentinelRef} />

                    {isLoading && !paginationEnabled && (
                        <div className="text-center my-2">
                            <div
                                className="spinner-border text-primary"
                                role="status"
                            >
                                <span className="visually-hidden">
                                    Loading...
                                </span>
                            </div>
                        </div>
                    )}

                    {isPaginationTotalStandalone && (
                        <PaginationTotalStandalone
                            {...paginationProps}
                        />
                    )}

                    {paginationEnabled && (
                        <div
                            className="pagination pagination-rounded justify-content-end"
                            style={{
                                marginTop: "-20px",
                                marginBottom: "40px"
                            }}
                        >
                            <PaginationListStandalone
                                {...paginationProps}
                            />
                        </div>
                    )}
                </div>
            )}
        </PaginationProvider>
    );
};


GlobalCustomTable.propTypes = {
    keyField: PropTypes.string.isRequired,
    data: PropTypes.array.isRequired,
    columns: PropTypes.array.isRequired,
    defaultSearchText: PropTypes.string,
    stablePagination: PropTypes.bool,
    controlledPagination: PropTypes.bool,
    classes: PropTypes.string,
    hideEmptyGroupHeadersOnSearch: PropTypes.bool,
};


export default React.memo(GlobalCustomTable);